import { startRegistration } from '@simplewebauthn/browser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/index.js';
import type { components } from '@/lib/api/schema.gen.js';
import { queryKeys } from '@/lib/query-client.js';
import { canCreatePasskey, PasskeyUnavailableError, runCeremony } from './passkey.api.js';

type Session = components['schemas']['Session'];
export type InvitationPreview = components['schemas']['InvitationPreview'];

interface Authenticated {
  status: 'AUTHENTICATED';
  session: Session;
}

interface CeremonyStart {
  ceremonyId: string;
  options: Record<string, unknown>;
}

/**
 * Looks up an invitation before anything is set up.
 *
 * A mutation rather than a query because it is a POST — the token goes in the
 * body so it does not end up repeated through server logs, proxy logs and
 * browser history on every call.
 */
export function usePreviewInvitation() {
  return useMutation({
    mutationFn: (token: string) =>
      api.post<InvitationPreview>('/auth/invitation/preview', { token }),
  });
}

function useAuthenticatingMutation<TInput>(mutationFn: (input: TInput) => Promise<Authenticated>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (result) => queryClient.setQueryData(queryKeys.session, result.session),
  });
}

/** Accepts an invitation by setting a first password. */
export function useAcceptInvitationWithPassword() {
  return useAuthenticatingMutation((input: { token: string; newPassword: string }) =>
    api.post<Authenticated>('/auth/invitation/accept', input),
  );
}

/**
 * Accepts an invitation by enrolling a passkey — so the new joiner never
 * creates a password at all, which is the point of ADR 0005.
 */
export function useAcceptInvitationWithPasskey() {
  return useAuthenticatingMutation(async (input: { token: string; label?: string }) => {
    if (!(await canCreatePasskey())) throw new PasskeyUnavailableError();

    const start = await api.post<CeremonyStart>('/auth/invitation/passkey/options', {
      token: input.token,
    });

    const response = await runCeremony(() =>
      startRegistration({ optionsJSON: start.options as never }),
    );

    return api.post<Authenticated>('/auth/invitation/passkey/verify', {
      token: input.token,
      ceremonyId: start.ceremonyId,
      response,
      ...(input.label ? { label: input.label } : {}),
    });
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (email: string) =>
      api.post<{ success: true }>('/auth/password/reset/request', { email }),
  });
}

export function useConfirmPasswordReset() {
  return useAuthenticatingMutation((input: { token: string; newPassword: string }) =>
    api.post<Authenticated>('/auth/password/reset/confirm', input),
  );
}

/** Replaces a password that expired, from the sign-in screen. */
export function useRotateExpiredPassword() {
  return useAuthenticatingMutation(
    (input: { email: string; currentPassword: string; newPassword: string }) =>
      api.post<Authenticated>('/auth/password/rotate', input),
  );
}

/** Changes the password of a signed-in employee. */
export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post<{ success: true }>('/auth/password/change', input),
  });
}
